/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {Fragment, useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useDispatch, useSelector} from 'react-redux'
import {Controller, useForm} from 'react-hook-form'
import {joiResolver} from '@hookform/resolvers/joi'
import {FiChevronDown, FiFile} from 'react-icons/fi'
import _ from 'lodash'
import {
  VmoCard,
  VmoCardContent,
  VmoForm,
  VmoFormCheckbox,
  VmoFormField,
  VmoFormSelect,
  VmoRadio,
  VmoTooltip,
} from 'vmo-library'
import {ADD_STATUS_TEMPLATES, GET_SINGLE_TEMPLATES, UPDATE_STATUS_TEMPLATES} from 'actions/types'
import {
  addStatusTemplates,
  getAllServices,
  getAllTemplates,
  getSingleTemplates,
  updateStatusTemplates,
} from 'actions/statuspage'
import Page from 'components/common/Page'
import {getPathWithPrefix, removeDoubleQuotes} from 'utils/helper'
import {servicesTypes} from 'constants/variables'
import {templatesSchema} from 'validation/status-page.schema'
import useUserPermissions from 'hooks/pure/useUserPermission'
import useLearnMoreUrl from 'hooks/pure/useLearnMoreUrl'
import {AiOutlineInfoCircle} from 'react-icons/ai'
import {STATUS_PAGE_PATH} from 'constants/path'
import {Input, TextArea} from 'components/common/Form'
import {TEMPLATES_TYPE_OPTIONS} from 'constants/statuspage'
import {useStatusPage} from 'hooks/pure/useStatusPage'

const defaultValues = {
  type: '',
  title: '',
  description: '',
  sourceId: '',
  notifySubscribers: false,
}

export const CreateTemplates = ({toggleForm}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {id: editId} = useParams()
  const {isLoadingData, type, successLabels = []} = useSelector(state => state.apiReducer)
  const {ServicesList = [], SingalTemplate} = useSelector(state => state.statuspage)

  const {allServiceByGroup = []} = ServicesList

  const [serviceId, setServiceId] = useState([])

  const {
    formState: {errors},
    control,
    handleSubmit,
    reset,
    clearErrors,
  } = useForm({
    mode: 'onTouched',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(templatesSchema),
    defaultValues,
  })

  const {statusPagePermissions} = useUserPermissions()
  const allowSettingsRole = statusPagePermissions?.settings

  const {statusPageLearnMoreUrl} = useLearnMoreUrl()
  const addTemplatesLearnMore = statusPageLearnMoreUrl?.templates?.addLearnMore
  const updateTemplatesLearnMore = statusPageLearnMoreUrl?.templates?.updateLearnMore

  const {sourceOptions} = useStatusPage()

  function updateCheckedService(action, id, serviceName, idIndex) {
    const list = [...serviceId]

    switch (action) {
      case 'add': {
        list.push({serviceId: id, serviceName, effect: 'OP'})
        break
      }
      case 'remove': {
        list.splice(idIndex, 1)
        break
      }

      default:
        break
    }

    setServiceId(list)
  }

  function onToggle(id, serviceName) {
    const serviceIdList = serviceId.map(e => e.serviceId)
    const idIndex = serviceIdList.findIndex(t => t === id)

    if (serviceIdList.indexOf(Number(id)) !== -1) {
      updateCheckedService('remove', id, serviceName, idIndex)
    } else {
      updateCheckedService('add', id, serviceName)
    }
  }

  const getValue = id => {
    const valueIndex = serviceId.findIndex(e => e.serviceId === id)
    return serviceId.map(e => (e.serviceId === id ? e.effect : e.effect))[valueIndex]
  }

  useEffect(() => {
    dispatch(getAllServices())
    if (editId) dispatch(getSingleTemplates(editId))
  }, [dispatch, editId])

  useEffect(() => {
    if (successLabels.includes(GET_SINGLE_TEMPLATES)) {
      reset(_.pick(SingalTemplate, ['type', 'title', 'description', 'sourceId', 'notifySubscribers']))
      setServiceId(SingalTemplate.effectedServices)
    }
    if (successLabels.includes(ADD_STATUS_TEMPLATES)) {
      toggleForm(false)
      dispatch(getAllTemplates())
    }
    if (successLabels.includes(UPDATE_STATUS_TEMPLATES)) {
      navigate(getPathWithPrefix(STATUS_PAGE_PATH.PREFIX, STATUS_PAGE_PATH.TEMPLATES))
      dispatch(getAllServices())
    }
  }, [successLabels, toggleForm, dispatch, reset, SingalTemplate, navigate])

  const submitData = data => {
    const payload = {
      ...data,
      effectedServices: serviceId,
    }

    if (!editId) dispatch(addStatusTemplates(payload))
    if (editId) dispatch(updateStatusTemplates(editId, payload))
  }

  return (
    <Page
      header={editId ? 'Edit Templates' : ''}
      subHeader="Create template for your incidents."
      table={false}
      learnMoreUrl={editId ? updateTemplatesLearnMore : addTemplatesLearnMore}
      headerDivider
      lockRole={!allowSettingsRole}
      actionButton={{
        cancel: {
          content: 'Cancel',
          onClick: () => {
            if (editId) {
              navigate(getPathWithPrefix(STATUS_PAGE_PATH.PREFIX, STATUS_PAGE_PATH.TEMPLATES))
              reset(defaultValues)
            } else {
              toggleForm(false)
              reset(defaultValues)
            }
          },
          disabled: isLoadingData && type.includes(UPDATE_STATUS_TEMPLATES),
          loading: isLoadingData && type.includes(UPDATE_STATUS_TEMPLATES),
        },
        success: {
          content: editId ? 'Update' : 'Save',
          ...(allowSettingsRole && {
            onClick: handleSubmit(submitData),
          }),
          disabled: isLoadingData && type.includes(UPDATE_STATUS_TEMPLATES),
          loading: isLoadingData && type.includes(UPDATE_STATUS_TEMPLATES),
        },
      }}
    >
      <VmoForm className="errorLabel" id="user-form">
        <VmoFormField required width={8}>
          <div className="info-header">
            <label>Template Type</label>
            <VmoTooltip
              content="Choose the type of template i.e incident or maintenance"
              trigger={<AiOutlineInfoCircle />}
            />
          </div>

          <Controller
            name="type"
            render={({field: {value, onChange, onBlur}}) => {
              return (
                <VmoFormSelect
                  fluid
                  options={TEMPLATES_TYPE_OPTIONS}
                  value={value}
                  onChange={(e, {value}) => {
                    onChange(value)
                  }}
                  onClick={() => errors.type && clearErrors('type')}
                  placeholder="Template type"
                  onBlur={onBlur}
                  disabled={!!editId}
                  error={
                    errors.type && {
                      content: removeDoubleQuotes(errors.type.message),
                    }
                  }
                />
              )
            }}
            control={control}
          />
        </VmoFormField>
        <Input
          label="Template Title"
          control={control}
          name="title"
          maxLength={30}
          placeholder="Template title"
          tooltipProps={{content: 'Specify title of the template'}}
          width={8}
          required
        />
        <TextArea
          name="description"
          control={control}
          label="Description"
          required
          width={8}
          style={{resize: 'none'}}
          maxLength={100}
          tooltipProps={{content: 'Specify brief description about the template'}}
          placeholder="Template description..."
          id="description"
        />
        <VmoFormField required width={8}>
          <div className="info-header">
            <label>Source</label>
            <VmoTooltip content="Set a source in template" trigger={<AiOutlineInfoCircle />} />
          </div>
          <Controller
            name="sourceId"
            render={({field: {value, onChange, onBlur}}) => {
              return (
                <VmoFormSelect
                  search
                  fluid
                  options={sourceOptions}
                  value={value}
                  placeholder="Source"
                  onChange={(e, {value}) => {
                    onChange(value)
                  }}
                  onClick={() => errors.sourceId && clearErrors('sourceId')}
                  onBlur={onBlur}
                  error={
                    errors.sourceId && {
                      content: removeDoubleQuotes(errors.sourceId.message),
                    }
                  }
                />
              )
            }}
            control={control}
          />
        </VmoFormField>

        <VmoCard fluid className="switchBox switchBoxSmall">
          <VmoCardContent>
            <div className="d-flex">
              <div className="d-flex" style={{justifyContent: 'flex-start'}}>
                <div className="checkbox-card-image">
                  <FiFile />
                </div>
                <div>
                  <h5>Notification</h5>
                  <p style={{width: '100%'}}>Send a Notification subscriber</p>
                </div>
              </div>

              <Controller
                control={control}
                name="notifySubscribers"
                render={({field: {value, onChange}}) => (
                  <VmoFormCheckbox toggle checked={value} onChange={(e, {checked}) => onChange(checked)} />
                )}
              />
            </div>
          </VmoCardContent>
        </VmoCard>
        <VmoCard fluid className="compoCard multi-compoCard select-services switchBoxSmall">
          <VmoCardContent>
            <h5>Select Services</h5>
            {allServiceByGroup
              .filter(service => service.type === 'service')
              .map((service, i) => (
                <Fragment key={service.id}>
                  <div className="d-flex mb-0 mt-3">
                    <div>
                      <label>{service.name}</label>
                      <p className="vmo-lead">Toggle for dashboard portal</p>
                    </div>
                    <VmoRadio
                      toggle
                      checked={serviceId.map(e => e.serviceId).includes(service.id)}
                      onChange={() => onToggle(service.id, service.name)}
                    />
                  </div>
                  {serviceId.some(e => e.serviceId === service.id) && (
                    <VmoFormField required width={10} className="mt-2">
                      <div className="info-header">
                        <label>Services Types</label>
                        <VmoTooltip
                          content="Select service type for dashboard portal"
                          trigger={<AiOutlineInfoCircle />}
                        />
                      </div>
                      <VmoFormSelect
                        search
                        fluid
                        options={servicesTypes}
                        defaultValue="OP"
                        value={getValue(service.id)}
                        placeholder="Services Types"
                        onChange={(e, {value}) =>
                          setServiceId(prv =>
                            prv.map(el => (el.serviceId === service.id ? {...el, effect: value} : el))
                          )
                        }
                      />
                    </VmoFormField>
                  )}
                </Fragment>
              ))}

            {allServiceByGroup
              .filter(service => service.type === 'group')
              .map((group, i) => (
                <Fragment key={group.id}>
                  <div className="d-flex" style={{margin: '18px 0'}}>
                    <div className="info mb-0">
                      <FiChevronDown className="multi-compoCard-toggle" />
                      <h5>{group.name}</h5>
                    </div>
                  </div>
                  {group?.services?.map((gServices, i) => (
                    <Fragment key={gServices.id}>
                      <div className="d-flex multi-compoCard-info">
                        <div className="info mb-0">
                          <div>
                            <label>{gServices.name}</label>
                            <p className="vmo-lead">Toggle for API services</p>
                          </div>
                        </div>
                        <div className="actBtn">
                          <VmoRadio
                            toggle
                            checked={serviceId.map(e => e.serviceId).includes(gServices.id)}
                            onChange={() => onToggle(gServices.id, gServices.name)}
                          />
                        </div>
                      </div>
                      {serviceId.some(e => e.serviceId === gServices.id) && (
                        <VmoFormField required width={10}>
                          <div className="info-header">
                            <label>Services Types</label>
                            <VmoTooltip
                              content="Select service type for API services"
                              trigger={<AiOutlineInfoCircle />}
                            />
                          </div>
                          <VmoFormSelect
                            search
                            fluid
                            options={servicesTypes}
                            defaultValue="OP"
                            value={getValue(gServices.id)}
                            placeholder="Services Types"
                            onChange={(e, {value}) =>
                              setServiceId(prv =>
                                prv.map(el => (el.serviceId === gServices.id ? {...el, effect: value} : el))
                              )
                            }
                          />
                        </VmoFormField>
                      )}
                    </Fragment>
                  ))}
                </Fragment>
              ))}
          </VmoCardContent>
        </VmoCard>
      </VmoForm>
    </Page>
  )
}
